export function dateFormatter(date: string): string
{
    ///date viene en forma dd/MM/YYYY
    let d = date.substring(0, 10).split("-");
    return d[2]+"/"+d[1]+"/"+d[0];
}

/**
 * Convierte un valor ISO date/datetime del API a {@link Date} o `null` si no es válido.
 * Las fechas sin hora (yyyy-MM-dd) se interpretan como medianoche local para evitar
 * desfases de un día al formatearlas con getters locales específicamente.
 * Usa la misma normalización de fracciones de segundo que {@link formatIsoDateTimeToDdMmYyyy}.
 */
export function parseIsoDate(value: unknown): Date | null
{
    if(value === null || value === undefined)
    {
        return null;
    }
    if(value instanceof Date)
    {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    const raw = String(value).trim();
    if(raw === "")
    {
        return null;
    }
    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if(dateOnlyMatch)
    {
        const year = Number(dateOnlyMatch[1]);
        const d = new Date(year, Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]));
        // El constructor de Date mapea los años 0-99 al rango 1900-1999 (0002 pasaría a ser 1902).
        // Al teclear una fecha en un input[type=date] los años intermedios caen en ese rango, así que
        // hay que preservar el año literal para no reescribir en el input algo distinto de lo tecleado.
        if(year < 100)
        {
            d.setFullYear(year);
        }
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const normalized = raw.replace(/(\.\d{3})\d+/, "$1");
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Formatea un {@link Date} como yyyy-MM-dd usando la fecha local, o `null` si no es válido.
 */
export function formatDateOnly(date: Date | null | undefined): string | null
{
    if(!date || Number.isNaN(date.getTime()))
    {
        return null;
    }
    // El año se rellena a 4 dígitos: un input[type=date] descarta el valor si no lo están,
    // lo que borraría la fecha a medio teclear.
    const y = String(date.getFullYear()).padStart(4, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

/**
 * Convierte ISO datetime del API (p. ej. 2026-03-11T10:00:00.3113) a dd/MM/yyyy.
 * Normaliza fracciones de segundo largas para que Date las parsee correctamente.
 */
export function formatIsoDateTimeToDdMmYyyy(value: unknown): string
{
    if(value === null || value === undefined)
    {
        return "—";
    }

    if(value instanceof Date)
    {
        if(Number.isNaN(value.getTime()))
        {
            return "—";
        }
        return formatDatePartsToDdMmYyyy(value);
    }

    const raw = String(value).trim();
    if(raw === "" || raw === "—")
    {
        return "—";
    }

    const normalized = raw.replace(/(\.\d{3})\d+/, "$1");
    const d = new Date(normalized);
    if(Number.isNaN(d.getTime()))
    {
        return raw;
    }
    return formatDatePartsToDdMmYyyy(d);
}

function formatDatePartsToDdMmYyyy(d: Date): string
{
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
}

/**
 * Convierte una fecha de competición (startDate / endDate) del API a {@link Date} a medianoche local,
 * o `null` si no es válida.
 *
 * El backend serializa los DateOnly de las competiciones con `ToString()` y la cultura del servidor, que en
 * producción es la estadounidense: "M/d/yyyy" (p. ej. "10/1/2026" es el 1 de octubre de 2026). Por eso el
 * formato con barras se interpreta SIEMPRE como mes/día/año, también con ceros ("10/01/2026"): nunca se intenta
 * adivinar dd/MM aunque los dos números sean menores o iguales que 12.
 *
 * Formatos aceptados:
 * - "M/d/yyyy" y "MM/dd/yyyy" (año de 4 dígitos).
 * - ISO date "yyyy-MM-dd".
 * - ISO datetime ("yyyy-MM-ddTHH:mm:ss[.fff][Z|±hh:mm]"), interpretado con {@link parseIsoDate} y truncado a la
 *   medianoche local del día resultante.
 * - Instancias de {@link Date} válidas (se devuelve una copia truncada a medianoche local).
 *
 * Se validan los rangos (mes 1-12 y día existente en ese mes): "2/30/2026" o "2026-02-30" devuelven `null`
 * en lugar de desbordarse al mes siguiente como haría el constructor de Date.
 * @param value Valor recibido del API (normalmente string); se recorta antes de analizarlo.
 * @returns La fecha a medianoche local o `null` si el valor no es una fecha válida.
 */
export function parseCompetitionDate(value: unknown): Date | null
{
    if(value === null || value === undefined)
    {
        return null;
    }
    if(value instanceof Date)
    {
        return Number.isNaN(value.getTime()) ? null : toLocalMidnight(value);
    }
    const raw = String(value).trim();
    if(raw === "")
    {
        return null;
    }

    // Formato del backend: mes/día/año, siempre en este orden
    const monthFirstMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if(monthFirstMatch)
    {
        return buildValidLocalDate(Number(monthFirstMatch[3]), Number(monthFirstMatch[1]), Number(monthFirstMatch[2]));
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})(T.*)?$/);
    if(isoMatch)
    {
        // Se validan los rangos del día literal: parseIsoDate aceptaría "2026-02-30" desbordándolo al 2 de marzo
        const literalDate = buildValidLocalDate(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]));
        if(literalDate === null || isoMatch[4] === undefined)
        {
            return literalDate;
        }
        const instant = parseIsoDate(raw);
        return instant === null ? null : toLocalMidnight(instant);
    }

    return null;
}

/**
 * Formatea una fecha de competición del API como "dd/MM/yyyy" (con ceros a la izquierda) usando
 * {@link parseCompetitionDate}.
 *
 * Si el valor no es una fecha válida devuelve una cadena vacía: antes, según la pantalla, se pintaba
 * "NaN/NaN/NaN", "undefined/undefined/..." o nada (o incluso se lanzaba una excepción con valores
 * `undefined`), así que la cadena vacía es la opción neutra que nunca muestra basura ni rompe el render.
 * @param value Valor recibido del API (normalmente "M/d/yyyy").
 * @returns La fecha en formato "dd/MM/yyyy" o "" si no es válida.
 */
export function formatCompetitionDate(value: unknown): string
{
    const date = parseCompetitionDate(value);
    return date === null ? "" : formatDatePartsToDdMmYyyy(date);
}

/**
 * Construye una fecha a medianoche local comprobando que año, mes y día forman una fecha real.
 */
function buildValidLocalDate(year: number, month: number, day: number): Date | null
{
    if(month < 1 || month > 12 || day < 1)
    {
        return null;
    }
    const date = new Date(year, month - 1, day);
    // El constructor de Date mapea los años 0-99 al rango 1900-1999; se preserva el año literal
    if(year < 100)
    {
        date.setFullYear(year);
    }
    if(date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
    {
        return null;
    }
    return date;
}

/**
 * Devuelve una copia de la fecha truncada a la medianoche local de su día natural.
 */
function toLocalMidnight(date: Date): Date
{
    const midnight = new Date(date.getTime());
    midnight.setHours(0, 0, 0, 0);
    return midnight;
}